import StaffBookingWarning from "@/components/StaffBookingWarning";
import StaffTimePicker from "@/components/StaffTimePicker";
import {DateJumpOptions} from '@/components/DateJumpOptions';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, CalendarDays } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatDuration, formatFullDate, toISODate } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DatePicker } from "@/components/ui/calendar";

export function StaffManualBooking() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const prefillDate = params.get("date") || "";
    const prefillTime = params.get("time") || "";
    const prefillCustomer = params.get('customer') || '';
    const [categories, setCategories] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);
    const [selectedCat, setSelectedCat] = useState(null);
    const [selectedServices, setSelectedServices] = useState([]);
    const [durationOverrides,setDurationOverrides]=useState({});
    const durations=Object.fromEntries(selectedServices.filter(s=>durationOverrides[s.id]!==undefined).map(s=>[s.id,durationOverrides[s.id]]));
    const durationKey=JSON.stringify(durations);
    const durationInvalid=Object.values(durations).some(n=>!Number.isInteger(Number(n))||Number(n)<5||Number(n)>720);
    const selectedSvc = selectedServices.length ? { ...selectedServices[0], name: selectedServices.map(s => s.name).join(' + '), duration: selectedServices.reduce((n,s) => n + (durations[s.id]!==undefined?Number(durations[s.id])*60:Number(s.duration || 0)), 0) } : null;
    const serviceKey = selectedServices.map(s => s.id).join(',');
    const [slotError, setSlotError] = useState('');
    const [requestId] = useState(() => globalThis.crypto?.randomUUID?.() || `staff-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const [uncertain, setUncertain] = useState(false);
    const [date, setDate] = useState(prefillDate);
    const [slots, setSlots] = useState(null);
    const [validTimes,setValidTimes]=useState(null);
    const [time, setTime] = useState(prefillTime);
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerResults, setCustomerResults] = useState([]);
    const [selectedCust, setSelectedCust] = useState(null);
    const [newCust, setNewCust] = useState(null);
    const [notes, setNotes] = useState("");
    const [notify, setNotify] = useState(true);
    const [depositMode, setDepositMode] = useState('automatic');
    const [familyPricing, setFamilyPricing] = useState(false);
    const [familyChoice, setFamilyChoice] = useState('free');
    const [familyAmount, setFamilyAmount] = useState('');
    const [busy, setBusy] = useState(false);
    const submitting=useRef(false);
    const [submitError, setSubmitError] = useState("");
    const [availabilityWarning, setAvailabilityWarning] = useState(null);
    const [conflictWarning,setConflictWarning]=useState(null),[acceptedAvailability,setAcceptedAvailability]=useState(null);
    useEffect(() => { setAvailabilityWarning(null);setConflictWarning(null);setAcceptedAvailability(null); }, [serviceKey, date, time,durationKey]);
    const [priceQuote,setPriceQuote] = useState(null), [priceError,setPriceError] = useState('');
    const [priceRefresh,setPriceRefresh] = useState(0);
    const agreedAmount = familyChoice === 'free' ? '0.00' : familyAmount;
    const familyInvalid = familyPricing && (!/^\d+(?:\.\d{1,2})?$/.test(agreedAmount) || !Number.isFinite(Number(agreedAmount)) || Number(agreedAmount) > Number(priceQuote?.price));
    useEffect(()=>{
        let active=true;setPriceQuote(null);setPriceError('');
        if(!serviceKey || !date)return;
        staffApi.bookingQuote(serviceKey.split(',').map(id=>({service_id:Number(id),quantity:1})),date)
            .then(q=>{if(active)setPriceQuote(q);}).catch(e=>{if(active)setPriceError(e.message || 'Could not check treatment prices.');});
        return ()=>{active=false;};
    },[serviceKey,date,priceRefresh]);

    useEffect(() => { staffApi.bookingCategories().then(setCategories).catch(setError); }, []);
    useEffect(()=>{
        let active=true;
        if(prefillCustomer) staffApi.getCustomer(prefillCustomer).then(c=>{if(active)setSelectedCust(c);}).catch(e=>{if(active)setError(e);});
        return()=>{active=false;};
    },[prefillCustomer]);

    useEffect(() => {
        if (!serviceKey || !date) return;
        let active = true;
        setSlots(null);setValidTimes(null); setSlotError('');
        if(durationInvalid){setSlots([]);setSlotError('Choose a treatment duration between 5 and 720 minutes.');return;}
        staffApi.bookingSlots(serviceKey.split(',').map(Number), date,JSON.parse(durationKey))
            .then(r => { if (active) {setSlots(r.slots || []);setValidTimes(r.valid_times||(r.slots||[]).map(s=>s.time));} })
            .catch(e => { if (active) { setSlots([]); setSlotError(e.message || 'Could not load availability.'); } });
        return () => { active = false; };
    }, [serviceKey, date,durationKey,durationInvalid]);

    useEffect(() => {
        if (!customerSearch) { setCustomerResults([]); return; }
        const t = setTimeout(() => {
            staffApi.listCustomers(customerSearch, 8).then((r) => setCustomerResults(r.customers || [])).catch(() => setCustomerResults([]));
        }, 300);
        return () => clearTimeout(t);
    }, [customerSearch]);

    const submit = async (overrideToken = null,conflictToken=null) => {
        if (submitting.current || busy || uncertain || !priceQuote || familyInvalid || durationInvalid) return;
        if (!selectedSvc || !date || !time || (!selectedCust && !newCust)) {
            toast.error("Please complete all steps"); return;
        }
        submitting.current=true;setBusy(true);
        setSubmitError("");
        try {
            const payload = {
                service_ids: selectedServices.map(s => s.id),
                duration_overrides:Object.fromEntries(Object.entries(durations).map(([id,n])=>[id,Number(n)])),
                request_id: requestId,
                expected_price: priceQuote.price,
                availability_override: typeof overrideToken === "string" ? overrideToken : acceptedAvailability,
                conflict_override:typeof conflictToken==='string'?conflictToken:null,
                date, time,
                internalNotes: notes,
                notifyParticipants: notify,
                deposit_request_mode: familyPricing ? 'manual' : depositMode,
                friends_family_amount: familyPricing ? agreedAmount : null,
            };
            if (selectedCust) payload.customerId = selectedCust.id;
            else payload.customer = newCust;
            const res = await staffApi.manualBooking(payload);
            const apptId = res?.appointment?.id;
            if (!apptId) throw new Error("The booking system did not confirm an appointment number. Check the calendar before trying again.");
            setConflictWarning(null);setAvailabilityWarning(null);
            toast.success(`Appointment created${apptId ? ` (#${apptId})` : ""}`);
            if (res.warning) toast.warning(res.warning, { duration: 12000 });
            nav(`/staff/calendar?d=${encodeURIComponent(date)}&v=day`, { replace: true });
        } catch (e) {
            if (e.detail?.code === 'availability_override_required') {
                setAvailabilityWarning(e.detail); return;
            }
            if(e.detail?.code==='conflict_override_required'){setConflictWarning(e.detail);return;}
            if(e.status===409 && /price/i.test(e.message || ''))setPriceRefresh(n=>n+1);
            if (!e.status || e.status >= 500) setUncertain(true);
            setSubmitError(e.status && e.status < 500
                ? e.message
                : `${e.message || "Booking could not be confirmed."} Check the calendar before trying again to avoid a duplicate booking.`);
        }
        finally { submitting.current=false;setBusy(false); }
    };

    const services = useMemo(() => selectedCat?.services || [], [selectedCat]);

    const categoriesForRender = categories;

    // Sprint 36.8 — split Date + Time into their own wizard steps so Angela
    // never has to scroll through a completed step. Advance is auto on
    // selection; back navigation preserves earlier choices.
    // Step 1 Category, Step 2 Service, Step 3 Date, Step 4 Time,
    // Step 5 Customer, Step 6 Notes & confirm.
    const goBack = () => {
        setStep(s => Math.max(1, s - 1));
    };

    const stepTitles = ["Category", "Treatment", "Date", "Time", "Customer", "Notes & confirm"];

    return (
        <div className="space-y-5" data-testid="staff-manual-booking">
            <div>
                <div className="flex items-center gap-3">
                    {step > 1 && (
                        <button onClick={goBack} data-testid="mb-back" className="h-9 w-9 rounded-full border hairline flex items-center justify-center text-white/70 hover:text-gold hover:border-gold/50" aria-label="Back">
                            <span className="text-lg leading-none">←</span>
                        </button>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">Step {step} of 6</div>
                        <h1 className="font-serif font-light text-3xl sm:text-4xl text-white leading-tight mt-0.5">{stepTitles[step - 1]}</h1>
                    </div>
                </div>
                {/* Compact breadcrumbs of previous selections — quick reference without scrolling */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-white/60" data-testid="mb-breadcrumbs">
                    {selectedCat && <button onClick={() => setStep(1)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{selectedCat.name}</button>}
                    {selectedSvc && <button onClick={() => setStep(2)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{selectedSvc.name}</button>}
                    {date && <button onClick={() => setStep(3)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{formatFullDate(date)}</button>}
                    {time && <button onClick={() => setStep(4)} className="rounded-full border hairline px-2 py-0.5 hover:border-gold/50 hover:text-gold">{time}</button>}
                </div>
                {step === 4 && (
                    <StepCard>
                        <p className="text-sm text-white mb-3">Start time · {formatDuration(selectedSvc?.duration || 0)} of treatments</p>
                        <StaffTimePicker label="Start time" value={time} onChange={setTime} data-testid="mb-edit-start" />
                        <div className="mt-4 space-y-3">{selectedServices.map(s=><label key={s.id} className="block text-sm text-white">{s.name} · duration (minutes)<input type="number" min="5" max="720" step="1" aria-label={`Duration for ${s.name}`} value={durations[s.id]??Math.round(Number(s.duration)/60)} onChange={e=>setDurationOverrides(v=>({...v,[s.id]:e.target.value===''?'':Number(e.target.value)}))} className="mt-1 block w-full rounded-xl bg-white text-navy p-3"/></label>)}<p className="text-xs text-white/60">Changes apply to this appointment only. The service duration and price stay unchanged.</p></div>
                        <p className="text-xs text-white/60 mt-3">Staff can book outside opening hours and during breaks. Existing appointments are checked before saving.</p>
                        {time && slots && validTimes && !slotError && !validTimes.includes(time) && <p role="alert" className="text-amber-200 text-sm mt-3">This time may overlap an appointment. You can shorten the duration above, or continue to review the conflict before overriding it. Past times cannot be booked.</p>}
                    </StepCard>
                )}
            </div>

            {availabilityWarning && <StaffBookingWarning title="Outside your service selection" message={availabilityWarning.message} busy={busy} error={submitError}
                onCancel={()=>setAvailabilityWarning(null)} onConfirm={()=>{setAcceptedAvailability(availabilityWarning.override_token);submit(availabilityWarning.override_token,conflictWarning?.override_token);setAvailabilityWarning(null);}}>
                {availabilityWarning.warnings.map((w,i)=><li key={i}><strong>{w.service_name}</strong><br/>{formatFullDate(w.date)} · {w.start}–{w.end}</li>)}
            </StaffBookingWarning>}
            {conflictWarning && !availabilityWarning && <StaffBookingWarning title="Appointment conflict" message={conflictWarning.message} busy={busy} error={submitError}
                onCancel={()=>setConflictWarning(null)} onConfirm={()=>submit(conflictWarning.availability_warning?.override_token||acceptedAvailability,conflictWarning.override_token)}>
                {conflictWarning.conflicts.map(c=><li key={c.id}><strong>{c.customer_name}</strong><br/>{c.service_name}<br/>{formatFullDate(c.start.slice(0,10))} · {c.start.slice(11,16)}–{c.end.slice(11,16)}</li>)}
                {conflictWarning.availability_warning&&<li>{conflictWarning.availability_warning.message}</li>}
            </StaffBookingWarning>}
            {error && <ErrorState error={error} onRetry={() => window.location.reload()} />}

            {!categories && !error && <Skeleton className="h-64" />}

            {categoriesForRender && (
                <div className="space-y-6">
                    {step === 1 && (
                        <StepCard>
                            {categoriesForRender.length === 0 ? (
                                <div className="text-xs text-white/60" data-testid="mb-no-cats">No services are available.</div>
                            ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mb-cat-grid">
                                {categoriesForRender.map((c) => {
                                    const active = selectedCat?.id === c.id;
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => { setSelectedCat(c); setStep(2); }}
                                            data-testid={`mb-cat-${c.id}`}
                                            className={`group w-full min-w-0 rounded-2xl border p-4 text-left transition-all flex items-center gap-4 ${active ? "border-gold bg-gold/10 ring-1 ring-gold/50" : "border-white/15 bg-white/[0.03] hover:border-gold/60 hover:bg-white/[0.06]"}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-white font-serif text-lg leading-tight break-words">{c.name}</div>
                                                <div className="text-[11px] uppercase tracking-[0.16em] text-gold/80 mt-1">{c.services?.length || 0} services</div>
                                            </div>
                                            <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${active ? "text-gold" : "text-white/40 group-hover:text-gold"}`} strokeWidth={2} />
                                        </button>
                                    );
                                })}
                            </div>
                            )}
                        </StepCard>
                    )}

                    {step === 2 && selectedCat && (
                        <StepCard>
                            <ul className="space-y-2" data-testid="mb-svc-list">
                                {services.map((s) => {
                                    const active = selectedServices.some(item => item.id === s.id);
                                    return (
                                        <li key={s.id}>
                                            <button
                                                onClick={() => { setSelectedServices(items => active ? items.filter(item => item.id !== s.id) : [...items, s]); }}
                                                aria-pressed={active} data-testid={`mb-svc-${s.id}`}
                                                className={`group w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${active ? "border-gold bg-gold/10 ring-1 ring-gold/50" : "border-white/15 bg-white/[0.03] hover:border-gold/60 hover:bg-white/[0.06]"}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white text-sm font-medium leading-snug break-words">{s.name}</div>
                                                    <div className="text-[11px] uppercase tracking-[0.14em] text-gold/85 mt-1">{formatDuration(s.duration)} · {formatPrice(s.price)}</div>
                                                </div>
                                                <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-colors ${active ? "text-gold" : "text-white/40 group-hover:text-gold"}`} strokeWidth={2} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className="mt-4 space-y-3">
                                <p className="text-sm text-white">{selectedServices.length} treatment{selectedServices.length === 1 ? '' : 's'} selected{selectedSvc ? ` · ${formatDuration(selectedSvc.duration)}` : ''}</p>
                                {selectedServices.map(item => <div key={item.id} className="flex justify-between gap-3 text-sm text-white"><span>{item.name}</span><button type="button" className="text-gold underline" onClick={() => setSelectedServices(items => items.filter(s => s.id !== item.id))}>Remove</button></div>)}
                                <button type="button" onClick={() => setStep(1)} className="block text-gold underline">Add from another category</button>
                                <button type="button" disabled={!selectedServices.length} data-testid="mb-continue-services" onClick={() => { setStep(prefillDate&&prefillTime?(selectedCust?6:5):3); }} className="w-full h-12 rounded-full bg-gold text-navy font-semibold disabled:opacity-40">Continue</button>
                            </div>
                        </StepCard>
                    )}

                    {step === 3 && selectedSvc && (
                        <StepCard>
                            <DateFieldPicker date={date} setDate={(v) => { setDate(v); setTime(''); }} />
                            {date && <button onClick={() => setStep(4)} data-testid="mb-continue-date" className="mt-4 h-11 px-6 rounded-full bg-gold text-navy font-semibold">Continue</button>}
                        </StepCard>
                    )}

                    {step === 4 && selectedSvc && date && (
                        <StepCard>
                            {slotError && <p role="alert" className="text-red-300">{slotError}</p>}
                            {time && slots && !slotError && !durationInvalid && <button type="button" onClick={() => setStep(5)} className="mb-4 w-full h-12 rounded-full bg-gold text-navy font-semibold" data-testid="mb-continue-time">Continue at {time}</button>}
                            {!slots ? <Skeleton className="h-20" /> : slots.length === 0 ? (
                                <div className="text-xs text-white/50">No available start times for that day.</div>
                            ) : (
                                <>
                                    <details><summary className="text-sm text-gold cursor-pointer mb-3">Show other available start times</summary>
                                    <div className="text-[11px] text-white/50 mb-2" data-testid="mb-slot-count">Suggested starts every 5 minutes. You can choose an exact time above.</div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="mb-slots">
                                        {slots.filter(s => Number(s.time.slice(3)) % 5 === 0).map((s) => (
                                            <button key={s.iso} onClick={() => { setTime(s.time); setStep(5); }} data-testid={`mb-slot-${s.time}`} className={`h-10 rounded-full border text-xs ${time === s.time ? "bg-gold text-navy border-gold" : "hairline text-white hover:border-gold/60"}`}>{s.time}</button>
                                        ))}
                                    </div></details>
                                </>
                            )}
                        </StepCard>
                    )}

                    {step === 5 && time && (
                        <StepCard>
                            <input type="text" placeholder="Search existing customer by name or email" value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCust(null); setNewCust(null); }} data-testid="mb-cust-search" className="w-full bg-transparent border hairline rounded-full px-4 py-3 text-white text-sm outline-none focus:border-gold" />
                            {customerResults.length > 0 && !selectedCust && (
                                <ul className="mt-2 space-y-1" data-testid="mb-cust-results">
                                    {customerResults.map((c) => (
                                        <li key={c.id}><button onClick={() => { setSelectedCust(c); setNewCust(null); setStep(6); }} data-testid={`mb-cust-pick-${c.id}`} className="w-full text-left rounded-lg border hairline p-2 text-sm text-white hover:border-gold/60">
                                            {c.firstName} {c.lastName} · <span className="text-white/50 text-xs">{c.email}</span>
                                        </button></li>
                                    ))}
                                </ul>
                            )}
                            {selectedCust && (
                                <div className="mt-2 rounded-lg border border-gold/40 p-3 text-sm text-white flex items-center justify-between gap-3" data-testid="mb-selected-customer">
                                    <span>Selected: {selectedCust.firstName} {selectedCust.lastName} · {selectedCust.email}</span>
                                    <button onClick={() => setStep(6)} className="h-9 px-4 rounded-full bg-gold text-navy text-xs font-semibold" data-testid="mb-continue-customer">Continue →</button>
                                </div>
                            )}
                            {!selectedCust && (
                                <details className="mt-4">
                                    <summary className="text-xs text-gold cursor-pointer" data-testid="mb-new-cust-toggle">Or create new customer</summary>
                                    <NewCustomerInline initialValue={newCust} onContinue={(v) => { setNewCust(v); setStep(6); }} />
                                </details>
                            )}
                        </StepCard>
                    )}

                    {step === 6 && (selectedCust || newCust) && (
                        <StepCard>
                            {priceQuote && <div className="mb-5 text-white"><p className="font-semibold">Treatment total: {formatPrice(priceQuote.price)}</p>{priceQuote.has_offers && <p className="text-gold">Offer applied · normally {formatPrice(priceQuote.original_price)} · save {formatPrice(priceQuote.original_price-priceQuote.price)}</p>}</div>}
                            {!priceQuote && !priceError && <p role="status">Checking current prices…</p>}
                            {priceError && <p role="alert">{priceError} <button type="button" className="underline" onClick={()=>setPriceRefresh(n=>n+1)}>Check prices again</button></p>}
                            <div className="mb-5 rounded-2xl border border-gold/40 p-4 text-white">
                                <label className="flex items-center gap-3 font-semibold">
                                    <input type="checkbox" data-testid="mb-family" checked={familyPricing} onChange={e=>setFamilyPricing(e.target.checked)} className="h-5 w-5 accent-gold" />
                                    Friends &amp; family
                                </label>
                                {familyPricing && <div className="mt-4 space-y-3">
                                    <p className="text-sm text-white/70">Set the total for this appointment. The normal service prices stay unchanged.</p>
                                    <div className="flex flex-wrap gap-3">
                                        <button type="button" data-testid="mb-family-free" aria-pressed={familyChoice==='free'} onClick={()=>setFamilyChoice('free')} className={`rounded-full border px-4 py-2 ${familyChoice==='free'?'bg-gold text-navy border-gold':'border-white/40'}`}>Free</button>
                                        <button type="button" data-testid="mb-family-custom" aria-pressed={familyChoice==='custom'} onClick={()=>setFamilyChoice('custom')} className={`rounded-full border px-4 py-2 ${familyChoice==='custom'?'bg-gold text-navy border-gold':'border-white/40'}`}>Custom amount</button>
                                    </div>
                                    {familyChoice==='custom' && <label className="block text-sm">Amount to charge (£)
                                        <input type="text" inputMode="decimal" data-testid="mb-family-amount" value={familyAmount} onChange={e=>setFamilyAmount(e.target.value)} className="mt-2 w-full rounded-xl bg-white p-3 text-navy" />
                                    </label>}
                                    {familyInvalid ? <p role="alert" className="text-amber-200 text-sm">Enter an amount from £0 up to the treatment total, with no more than two decimal places.</p>
                                        : <p className="font-semibold">Appointment total: {formatPrice(Number(agreedAmount))}</p>}
                                    <p className="text-sm text-white/70">No deposit will be requested. Any amount is due at the appointment. Only staff can choose this price.</p>
                                </div>}
                            </div>
                            <textarea placeholder="Internal notes (visible to staff only)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mb-notes" rows={3} className="w-full bg-transparent border hairline rounded-2xl p-3 text-white text-sm outline-none focus:border-gold" />
                            <label className="mt-3 flex items-center gap-2 text-sm text-white/70">
                                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="mb-notify" /> Send booking notifications
                            </label>
                            {!familyPricing && <><label className="block text-white text-sm mt-5">
                                Deposit request
                                <select value={depositMode} onChange={e => setDepositMode(e.target.value)} data-testid="mb-deposit-mode" className="mt-2 w-full rounded-2xl bg-white text-slate-900 p-3">
                                    <option value="automatic">Automatically email the deposit request</option>
                                    <option value="manual">Manual — send later or record cash / bank payment</option>
                                </select>
                            </label>
                            <p className="text-white/70 text-sm mt-2">{depositMode === 'automatic'
                                ? 'After saving, email a request for any unpaid service deposit. Services with no deposit are skipped.'
                                : 'No deposit request will be sent. Record money received on the appointment’s payment card; choosing Manual does not mark it paid.'}</p></>}
                            {submitError && <div role="alert" className="mt-4 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                                {submitError}
                                <button type="button" className="block mt-2 underline font-semibold" onClick={() => nav(`/staff/calendar?d=${encodeURIComponent(date)}&v=day`)}>Check calendar</button>
                            </div>}
                            <button onClick={submit} disabled={busy || uncertain || !priceQuote || familyInvalid} data-testid="mb-submit" className="mt-6 w-full h-12 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                {busy ? "Creating..." : uncertain ? "Check calendar before retrying" : "Create appointment"}
                            </button>
                        </StepCard>
                    )}
                </div>
            )}
        </div>
    );
}

function StepCard({ children }) {
    return (
        <section className="rounded-2xl glass p-4 sm:p-5" data-testid="mb-step-card">
            {children}
        </section>
    );
}

function StepSection({ num, title, open, children }) {
    // Sprint 36.8 — kept for backwards-compat but unused by the new wizard.
    return (
        <section className={`rounded-2xl glass p-4 sm:p-5 ${open ? "" : "opacity-40 pointer-events-none"}`} data-testid={`mb-step-${num}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85 mb-2">Step {num}</div>
            <h3 className="font-serif text-xl text-white mb-3">{title}</h3>
            {children}
        </section>
    );
}

/**
 * Sprint 36.11 — Themed date picker for the wizard.
 *
 * The old native <input type=date> rendered as a stark white rectangle
 * against the navy card because Chromium's UA styling for date inputs
 * ignores `bg-transparent` + `text-white` on the value display. Replaced
 * with a shadcn Popover + Calendar that matches the Pout design system
 * exactly (navy trigger, gold accent, calendar icon, formatted label).
 */
function DateFieldPicker({ date, setDate }) {
    const [open, setOpen] = React.useState(false);
    const selected = date ? new Date(`${date}T00:00:00`) : undefined;
    const label = selected
        ? selected.toLocaleDateString('en-GB', { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : "Pick a date";
    const todayISO = toISODate(new Date());
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="mb-date"
                    aria-label="Choose booking date"
                    className={`w-full sm:w-auto min-w-[280px] rounded-2xl border px-4 py-3 text-left flex items-center gap-3 transition-all ${date ? "border-gold/60 bg-gold/10" : "border-white/20 bg-white/[0.03] hover:border-gold/50"}`}
                >
                    <CalendarDays className={`h-5 w-5 flex-shrink-0 ${date ? "text-gold" : "text-white/60"}`} strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Booking date</div>
                        <div className={`text-sm font-medium truncate ${date ? "text-white" : "text-white/60"}`}>{label}</div>
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="app-light w-auto max-h-[85dvh] overflow-y-auto p-3 bg-white border border-navy/15 shadow-lg rounded-xl text-navy" data-testid="mb-date-popover">
                <DateJumpOptions anchor={selected||new Date()} onSelect={day=>{setDate(toISODate(day));setOpen(false);}}/>
                <DatePicker
                    mode="single"
                    selected={selected}
                    onSelect={(d) => {
                        if (!d) return;
                        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        setDate(iso);
                        setOpen(false);
                    }}
                    defaultMonth={selected || new Date()}
                    numberOfMonths={2}
                    disabled={(d) => {
                        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        return iso < todayISO;
                    }}
                    initialFocus
                    showOutsideDays={true}
                />
            </PopoverContent>
        </Popover>
    );
}


function NewCustomerInline({ initialValue, onContinue }) {
    const [f, setF] = useState(initialValue || { firstName: "", lastName: "", email: "", phone: "", countryPhoneIso: "gb" });
    const valid = f.firstName.trim() && f.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.phone.replace(/\D/g, '').length >= 7;
    return (
        <div className="mt-3 grid grid-cols-2 gap-3" data-testid="mb-new-cust-form">
            <input placeholder="First name" value={f.firstName} onChange={(e) => setF({...f, firstName: e.target.value})} data-testid="mb-nc-first" className="bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Last name" value={f.lastName} onChange={(e) => setF({...f, lastName: e.target.value})} data-testid="mb-nc-last" className="bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Email" type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="mb-nc-email" className="col-span-2 bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <input placeholder="Phone" type="tel" value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})} data-testid="mb-nc-phone" className="col-span-2 bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
            <button type="button" disabled={!valid} data-testid="mb-new-client-continue" onClick={() => onContinue(Object.fromEntries(Object.entries(f).map(([k,v]) => [k,v.trim()])))} className="col-span-2 h-12 rounded-full bg-gold text-navy font-semibold disabled:opacity-40">Continue with this client</button>
        </div>
    );
}


